<template>
  <!-- Phase 6: polish + check-average bulk entry (shell dialog). -->
  <div class="cklm" :class="{ 'cklm--selecting': selectionMode }" dir="rtl">
    <div class="cklm__header">
      <div class="cklm__bar">
        <div class="cklm__bar-actions">
          <button
            v-if="checksList.type !== 'removedChecks'"
            type="button"
            class="cklm__bar-btn cklm__bar-btn--create"
            :aria-label="$t(checksList.type)"
            @click="goToNew"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            type="button"
            class="cklm__bar-btn"
            :aria-label="$t('refresh')"
            @click="onRefreshTap"
          >
            <v-icon size="20" color="#37474F">mdi-refresh</v-icon>
          </button>
        </div>
        <h1 class="cklm__title">
          {{
            checksList.titleProp
              ? $t(checksList.titleProp)
              : $t($route.name)
          }}
        </h1>
        <button
          type="button"
          class="cklm__bar-btn cklm__bar-btn--back"
          :aria-label="$t('back')"
          @click="goBack"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>

      <div class="cklm__search-row">
        <div class="cklm__search">
          <v-icon size="20" color="#90A4AE" class="cklm__search-icon">
            mdi-magnify
          </v-icon>
          <input
            v-model="checksList.search"
            type="search"
            class="cklm__search-input"
            :placeholder="$t('search')"
            autocomplete="off"
          />
          <button
            v-if="checksList.search"
            type="button"
            class="cklm__search-clear"
            :aria-label="$t('clear')"
            @click="checksList.search = ''"
          >
            <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
          </button>
        </div>
        <button
          type="button"
          class="cklm__filter-btn"
          :class="{ 'cklm__filter-btn--active': hasAnyFilter }"
          :aria-label="$t('filter')"
          @click="onFilterTap"
        >
          <v-icon
            size="22"
            :color="hasAnyFilter ? '#1565C0' : '#546E7A'"
          >
            mdi-filter-outline
          </v-icon>
        </button>
        <button
          type="button"
          class="cklm__filter-btn"
          :class="{ 'cklm__filter-btn--active': selectionMode }"
          :aria-label="$t('select')"
          @click="toggleSelectionMode"
        >
          <v-icon size="22" :color="selectionMode ? '#1565C0' : '#546E7A'">
            mdi-checkbox-multiple-marked-outline
          </v-icon>
        </button>
      </div>

      <div v-if="selectionMode" class="cklm__select-bar" dir="rtl">
        <button
          type="button"
          class="cklm__select-bar-btn"
          @click="selectAllLoaded"
        >
          <v-icon size="17" color="#5B7C99">mdi-check-all</v-icon>
          {{ $t("selectAllItems") }}
        </button>
        <span class="cklm__select-count">
          {{ selectedItems.length }} {{ $t("selectedCount") }}
        </span>
        <button
          type="button"
          class="cklm__select-bar-btn"
          @click="clearSelection"
        >
          <v-icon size="17" color="#90A4AE">mdi-close</v-icon>
          {{ $t("deselectAll") }}
        </button>
      </div>
    </div>

    <div
      v-if="checksList.loading && !displayItems.length"
      class="cklm__state"
    >
      <v-progress-circular indeterminate color="#5B7C99" size="28" />
    </div>
    <div v-else-if="!displayItems.length" class="cklm__state cklm__state--empty">
      <img
        class="cklm__empty-logo"
        src="@/assets/dashboard/no-data-cloud.png"
        alt=""
      />
      <p class="cklm__empty-text">{{ $t("no-data") }}</p>
    </div>
    <div v-else class="cklm__list">
      <article
        v-for="(item, index) in displayItems"
        :key="item.id || index"
        class="cklm-card"
        :class="{
          'cklm-card--selected': selectionMode && isSelected(item),
        }"
      >
        <div
          v-if="selectionMode"
          class="cklm-card__select-overlay"
          :class="{ 'cklm-card__select-overlay--on': isSelected(item) }"
          @click="toggleSelect(item)"
        >
          <span v-if="isSelected(item)" class="cklm-card__check">
            <v-icon size="17" color="#FFFFFF">mdi-check</v-icon>
          </span>
        </div>
        <div class="cklm-card__head">
          <div class="cklm-card__title-block">
            <button
              type="button"
              class="cklm-card__name-link"
              :disabled="!canEdit(item)"
              @click="checksList.edit(item)"
            >
              <span class="cklm-card__name">
                {{ item.account && item.account.name ? item.account.name : "—" }}
              </span>
              <v-icon
                v-if="item.lock"
                size="14"
                color="#E53935"
                class="ms-1"
              >
                mdi-lock-outline
              </v-icon>
              <v-icon
                v-else-if="canEdit(item)"
                size="12"
                color="#879EB6"
              >
                mdi-pencil-outline
              </v-icon>
            </button>
            <div class="cklm-card__sub">
              {{ $t("number") }} {{ $t("check") }}: {{ item.number || "—" }}
              · {{ $t("due") }}: {{ item.due_date || "—" }}
            </div>
          </div>
          <span class="cklm-card__number">#{{ item.id }}</span>
        </div>

        <div class="cklm-card__amounts">
          <div class="cklm-card__amount-row">
            <span class="cklm-card__amount-label">{{ $t("amount") }}</span>
            <span class="cklm-card__amount-value cklm-card__amount-value--money">
              {{ amountText(item) }}
            </span>
          </div>
          <div class="cklm-card__amount-row">
            <span class="cklm-card__amount-label">{{ $t("bank") }}</span>
            <span class="cklm-card__amount-value">{{ bankText(item) }}</span>
          </div>
          <div class="cklm-card__amount-row">
            <span class="cklm-card__amount-label">{{ $t("status") }}</span>
            <span
              class="cklm-card__status-pill"
              :class="{ 'cklm-card__status-pill--empty': statusLabel(item) === '—' }"
              :style="
                statusLabel(item) === '—' ? null : statusChipStyle(item)
              "
            >
              {{ statusLabel(item) }}
            </span>
          </div>
          <div class="cklm-card__amount-row">
            <span class="cklm-card__amount-label">{{ $t("date") }}</span>
            <span class="cklm-card__amount-value">{{ item.date || "—" }}</span>
          </div>
        </div>

        <div v-if="!selectionMode" class="cklm-card__footer">
          <div class="cklm-card__actions">
            <button
              v-if="checksList.type !== 'removedChecks'"
              type="button"
              class="cklm-btn cklm-btn--danger"
              :disabled="!canDelete(item) || !!item.lock"
              @click="askDelete(item)"
            >
              <v-icon size="14" color="#E53935">mdi-trash-can-outline</v-icon>
              {{ $t("remove") }}
            </button>
            <button
              v-if="checksList.type !== 'removedChecks'"
              type="button"
              class="cklm-btn cklm-btn--primary"
              :disabled="!canEdit(item)"
              @click="checksList.edit(item)"
            >
              <v-icon size="14" color="#FFFFFF">mdi-pencil-outline</v-icon>
              {{ $t("edit") }}
            </button>
            <button
              type="button"
              class="cklm-btn cklm-btn--outline"
              @click="openDetails(item)"
            >
              <v-icon size="14" color="#5B7C99">mdi-eye-outline</v-icon>
              {{ $t("details") }}
            </button>
          </div>
          <button
            v-if="canChangeStatus(item)"
            type="button"
            class="cklm-pay-btn"
            @click="openStatusSheet(item)"
          >
            <v-icon size="14" color="#5B7C99">mdi-swap-horizontal</v-icon>
            {{ $t("change") }} {{ $t("status") }}
          </button>
        </div>
      </article>
      <div v-if="appending" class="cklm__state cklm__state--more">
        <v-progress-circular indeterminate color="#5B7C99" size="22" />
      </div>
    </div>

    <div ref="sentinel" class="cklm__sentinel" />

    <div v-if="selectionMode" class="cklm-bulkbar" dir="rtl">
      <button
        v-if="lockPermission && checksList.type !== 'removedChecks'"
        type="button"
        class="cklm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('lock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-outline</v-icon>
        {{ $t("lock") }}
      </button>
      <button
        v-if="lockPermission && checksList.type !== 'removedChecks'"
        type="button"
        class="cklm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('unlock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-open-outline</v-icon>
        {{ $t("unlock") }}
      </button>
      <button
        type="button"
        class="cklm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkCheckAverage"
      >
        <v-icon size="19" color="#5B7C99">mdi-calendar-clock</v-icon>
        {{ $t("checks-count") }}
      </button>
      <button
        v-if="checksList.type === 'removedChecks'"
        type="button"
        class="cklm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkRefreshRemoved"
      >
        <v-icon size="19" color="#5B7C99">mdi-delete-restore</v-icon>
        {{ $t("refreshRemovedChecks") }}
      </button>
      <button
        v-if="
          checksList.type !== 'removedChecks' &&
          removePermission(checksList.type)
        "
        type="button"
        class="cklm-bulkbar__btn cklm-bulkbar__btn--danger"
        :disabled="!selectedItems.length"
        @click="askGroupDelete"
      >
        <v-icon size="19" color="#E53935">mdi-trash-can-outline</v-icon>
        {{ $t("groupDelete") }}
      </button>
    </div>

    <v-bottom-sheet v-model="detailSheet" scrollable>
      <v-card class="cklm-sheet">
        <div class="cklm-sheet__handle" />
        <div class="cklm-sheet__header" dir="rtl">
          <div class="cklm-sheet__header-side">
            <span v-if="detailItem" class="cklm-sheet__num-chip">
              #{{ detailItem.id }}
            </span>
          </div>
          <div class="cklm-sheet__title">{{ $t("details") }}</div>
          <div class="cklm-sheet__header-side cklm-sheet__header-side--end">
            <button
              type="button"
              class="cklm-sheet__back"
              :aria-label="$t('back')"
              @click="detailSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <div v-if="detailItem" class="cklm-sheet__quickbar" dir="rtl">
          <button
            v-if="checksList.type !== 'removedChecks'"
            type="button"
            class="cklm-sheet__quick"
            :disabled="!canEdit(detailItem)"
            @click="runNamedAction('edit')"
          >
            <v-icon size="18" color="#5B7C99">mdi-pencil-outline</v-icon>
            {{ $t("edit") }}
          </button>
          <button
            v-if="canChangeStatus(detailItem)"
            type="button"
            class="cklm-sheet__quick"
            @click="openStatusSheet(detailItem)"
          >
            <v-icon size="18" color="#5B7C99">mdi-swap-horizontal</v-icon>
            {{ $t("change") }} {{ $t("status") }}
          </button>
          <button
            v-if="lockPermission && checksList.type !== 'removedChecks'"
            type="button"
            class="cklm-sheet__quick"
            @click="runNamedAction('lock')"
          >
            <v-icon
              size="18"
              :color="detailItem.lock ? 'green' : 'pink'"
            >
              {{
                detailItem.lock
                  ? "mdi-lock-open-outline"
                  : "mdi-lock-outline"
              }}
            </v-icon>
            {{ detailItem.lock ? $t("unlock") : $t("lock") }}
          </button>
          <button
            v-if="checksList.type !== 'removedChecks'"
            type="button"
            class="cklm-sheet__quick"
            v-clipboard:copy="copyLink(detailItem)"
            @click="onCopyLinkQuick"
          >
            <v-icon size="18" color="#5B7C99">mdi-link</v-icon>
            {{ $t("copy-link") }}
          </button>
          <button
            type="button"
            class="cklm-sheet__quick"
            @click="moreSheet = true"
          >
            <v-icon size="18" color="#5B7C99">mdi-dots-horizontal</v-icon>
            {{ $t("more") }}
          </button>
        </div>

        <div v-if="detailItem" class="cklm-sheet__name-block" dir="rtl">
          <div class="cklm-sheet__name-row">
            <span class="cklm-sheet__row-label">{{ $t("account") }}</span>
            <span class="cklm-sheet__name-value">
              {{
                detailItem.account && detailItem.account.name
                  ? detailItem.account.name
                  : "—"
              }}
            </span>
          </div>
          <button
            v-if="detailItem.account && detailItem.account.fin_id"
            type="button"
            class="cklm-sheet__linkrow"
            @click="openAccountTurnover(detailItem)"
          >
            <v-icon size="18" color="#00796B">mdi-playlist-check</v-icon>
            <span class="cklm-sheet__linkrow-text">{{ $t("turnover") }}</span>
            <v-icon size="18" color="#80CBC4">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div v-if="detailItem" class="cklm-sheet__body" dir="rtl">
          <div class="cklm-sheet__card">
            <div
              v-for="(row, i) in detailRows(detailItem)"
              :key="'detail-row-' + i"
              class="cklm-sheet__row"
              :class="{
                'cklm-sheet__row--band':
                  row.kind === 'band' || row.kind === 'amount',
                'cklm-sheet__row--money': row.kind === 'amount',
              }"
            >
              <span class="cklm-sheet__row-label">{{ row.label }}</span>
              <span class="cklm-sheet__row-value">{{ row.value }}</span>
            </div>

            <div class="cklm-sheet__row">
              <span class="cklm-sheet__row-label">{{ $t("relations") }}</span>
              <span
                v-if="detailItem.relations && detailItem.relations.length"
                class="cklm-sheet__tags"
              >
                <v-chip
                  v-for="(relation, ri) in detailItem.relations"
                  :key="'detail-rel-' + ri"
                  :color="relation.color || '#607D8B'"
                  label
                  outlined
                  small
                  class="px-1"
                >
                  {{ relation.name }}
                </v-chip>
              </span>
              <span v-else class="cklm-sheet__row-value">—</span>
            </div>

            <div class="cklm-sheet__row">
              <span class="cklm-sheet__row-label">{{ $t("tag") }}</span>
              <span
                v-if="detailItem.tags && detailItem.tags.length"
                class="cklm-sheet__tags"
              >
                <v-chip
                  v-for="(tag, ti) in detailItem.tags"
                  :key="'detail-tag-' + ti"
                  :color="tag.color || '#607D8B'"
                  label
                  small
                  class="px-1"
                >
                  <span class="px-1 white--text">{{ tag.text }}</span>
                </v-chip>
              </span>
              <span v-else class="cklm-sheet__row-value">—</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="moreSheet">
      <v-card class="cklm-sheet">
        <div class="cklm-sheet__handle" />
        <div class="cklm-sheet__header" dir="rtl">
          <span class="cklm-sheet__header-spacer" />
          <div class="cklm-sheet__title">{{ $t("more") }}</div>
          <button
            type="button"
            class="cklm-sheet__back"
            :aria-label="$t('back')"
            @click="moreSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div v-if="detailItem" class="cklm-sheet__body" dir="rtl">
          <div class="cklm-sheet__actions cklm-sheet__actions--sheet">
            <button
              v-if="detailItem.doc_head_id"
              type="button"
              class="cklm-sheet__action"
              @click="runNamedAction('viewDoc')"
            >
              <v-icon size="20" color="#5B7C99">mdi-file-eye-outline</v-icon>
              <span>{{ $t("viewAccountingDoc") }}</span>
            </button>
            <button
              v-if="checksList.receiptKind"
              type="button"
              class="cklm-sheet__action"
              @click="runNamedAction('receipt')"
            >
              <v-icon size="20" color="#5B7C99">mdi-receipt-text-outline</v-icon>
              <span>{{ $t("treasuryReceiptPrint") }}</span>
            </button>
            <button
              v-if="checksList.type === 'payCheck'"
              type="button"
              class="cklm-sheet__action"
              @click="runNamedAction('chequePrint')"
            >
              <v-icon size="20" color="#5B7C99">mdi-checkbook</v-icon>
              <span>{{ $t("chequeLeafPrint") }}</span>
            </button>
            <button
              v-if="
                checksList.type !== 'removedChecks' && detailItem.doc_head_id
              "
              type="button"
              class="cklm-sheet__action"
              @click="runNamedAction('print')"
            >
              <v-icon size="20" color="#5B7C99">mdi-printer-outline</v-icon>
              <span>{{ $t("print") }} {{ $t("document") }}</span>
            </button>
            <div class="cklm-sheet__action cklm-sheet__action--log">
              <log
                :title-prop="$t('history')"
                icon-prop="mdi-history"
                :listProp="true"
                :type-prop="logType"
                :refIdProp="detailItem.id"
                mode-prop="button"
              />
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Layered filter: same shell fields as desktop (no column picker). -->
    <v-bottom-sheet v-model="filterSheet" scrollable>
      <v-card class="cklm-sheet cklm-sheet--filter">
        <div class="cklm-sheet__handle" />
        <div class="cklm-sheet__header" dir="rtl">
          <span class="cklm-sheet__header-spacer" />
          <div class="cklm-sheet__title">{{ $t("filter") }}</div>
          <button
            type="button"
            class="cklm-sheet__back"
            :aria-label="$t('back')"
            @click="filterSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="cklm-filter-nav" dir="rtl">
          <button
            v-for="group in filterGroups"
            :key="group.key"
            type="button"
            class="cklm-filter-nav__item"
            @click="openFilterGroup(group.key)"
          >
            <v-icon class="cklm-filter-nav__icon" size="22" color="#5B7C99">
              {{ group.icon }}
            </v-icon>
            <span class="cklm-filter-nav__texts">
              <span class="cklm-filter-nav__item-title">{{ group.title }}</span>
              <span class="cklm-filter-nav__item-sub">
                {{ groupSubtitle(group.key) }}
              </span>
            </span>
            <span v-if="groupHasValue(group.key)" class="cklm-filter-nav__dot" />
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div class="cklm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="cklm-sheet__btn cklm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="cklm-sheet__btn cklm-sheet__btn--ghost"
            @click="clearFilter"
          >
            <v-icon size="16" color="#5B7C99">mdi-filter-remove-outline</v-icon>
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="filterGroupSheet" scrollable>
      <v-card class="cklm-sheet cklm-sheet--filter">
        <div class="cklm-sheet__handle" />
        <div class="cklm-sheet__header" dir="rtl">
          <span class="cklm-sheet__header-spacer" />
          <div class="cklm-sheet__title">{{ activeFilterGroupTitle }}</div>
          <button
            type="button"
            class="cklm-sheet__back"
            :aria-label="$t('back')"
            @click="backToFilterMain"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="cklm-filter-fields" dir="rtl">
          <template v-if="filterGroup === 'parties'">
            <div class="cklm-f-field-wrap">
              <div class="cklm-f-field-wrap__label">
                {{ accountFilterTitle }}
              </div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="openPicker('ckAccount')"
              >
                <span class="cklm-f-picker-field__text">
                  {{ pickerSummary("ckAccount", checksList.account) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cklm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="ckAccount"
                  v-model="checksList.account"
                  :title="accountFilterTitle"
                  type="person"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>

            <div
              v-if="checksList.type === 'payCheck'"
              class="cklm-f-field-wrap"
            >
              <div class="cklm-f-field-wrap__label">{{ $t("bank") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="openPicker('ckBank')"
              >
                <span class="cklm-f-picker-field__text">
                  {{ pickerSummary("ckBank", checksList.bank) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cklm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="ckBank"
                  v-model="checksList.bank"
                  :title="$t('bank')"
                  type="bank"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>

            <div
              v-if="checksList.type === 'recieveCheck'"
              class="cklm-f-field-wrap"
            >
              <div class="cklm-f-field-wrap__label">{{ $t("bank") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="openPicker('ckBankRecieve')"
              >
                <span class="cklm-f-picker-field__text">
                  {{ pickerSummary("ckBankRecieve", checksList.bankRecieve) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cklm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="ckBankRecieve"
                  v-model="checksList.bankRecieve"
                  :title="$t('bank')"
                  type="bankCheckRecieve"
                  :filter="false"
                  :multiple="true"
                />
              </div>
            </div>

            <div class="cklm-f-field-wrap">
              <div class="cklm-f-field-wrap__label">{{ $t("currency") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="openPicker('ckCurrency')"
              >
                <span class="cklm-f-picker-field__text">
                  {{ pickerSummary("ckCurrency", checksList.currencyFilter) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cklm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="ckCurrency"
                  v-model="checksList.currencyFilter"
                  :title="$t('currency')"
                  type="currency"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>

            <div
              v-if="checksList.type === 'recieveCheck'"
              class="cklm-f-field-wrap"
            >
              <div class="cklm-f-field-wrap__label">{{ $t("cashBox") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="openPicker('ckCashBox')"
              >
                <span class="cklm-f-picker-field__text">
                  {{ pickerSummary("ckCashBox", checksList.cashBox) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cklm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="ckCashBox"
                  v-model="checksList.cashBox"
                  :title="$t('cashBox')"
                  type="getCash"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'date'">
            <div
              v-for="field in dateFields"
              :key="field.key"
              class="cklm-f-field-wrap"
            >
              <div class="cklm-f-field-wrap__label">{{ field.label }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="openDateLayer(field)"
              >
                <span class="cklm-f-picker-field__text">
                  {{
                    hasValue(checksList[field.key])
                      ? checksList[field.key]
                      : "—"
                  }}
                </span>
                <span
                  v-if="hasValue(checksList[field.key])"
                  class="cklm-picker-cover__clear"
                  @click.stop="checksList[field.key] = null"
                >
                  <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>

          <template v-else-if="filterGroup === 'category'">
            <div class="cklm-f-field-wrap">
              <div class="cklm-f-field-wrap__label">{{ $t("tag") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="openPicker('ckTag')"
              >
                <span class="cklm-f-picker-field__text">
                  {{ pickerSummary("ckTag", checksList.tag) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cklm-picker-host" aria-hidden="true">
                <tag ref="ckTag" v-model="checksList.tag" />
              </div>
            </div>
            <div class="cklm-f-field-wrap">
              <div class="cklm-f-field-wrap__label">{{ $t("branch") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="openPicker('ckBranch')"
              >
                <span class="cklm-f-picker-field__text">
                  {{ pickerSummary("ckBranch", checksList.branch) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cklm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="ckBranch"
                  v-model="checksList.branch"
                  title="branch"
                  type="branch"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="cklm-f-field-wrap">
              <div class="cklm-f-field-wrap__label">{{ $t("registerar") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="openPicker('ckRegisterar')"
              >
                <span class="cklm-f-picker-field__text">
                  {{ pickerSummary("ckRegisterar", checksList.registerar) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cklm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="ckRegisterar"
                  v-model="checksList.registerar"
                  :title="$t('registerar')"
                  type="partners"
                  :multiple="true"
                  :filter="true"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'status'">
            <div class="cklm-f-field-wrap">
              <div class="cklm-f-field-wrap__label">{{ $t("status") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="
                  openSelectLayer(
                    'status',
                    $t('status'),
                    checksList.statusItems || [],
                    'text',
                    true
                  )
                "
              >
                <span class="cklm-f-picker-field__text">
                  {{
                    selectSummary(
                      "status",
                      checksList.statusItems || [],
                      "text"
                    )
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
            <div class="cklm-f-field-wrap">
              <div class="cklm-f-field-wrap__label">
                {{ $t("lockStatusDocuments") }}
              </div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="
                  openSelectLayer(
                    'lockStatus',
                    $t('lockStatusDocuments'),
                    checksList.lockStatusItems || [],
                    'text',
                    false
                  )
                "
              >
                <span class="cklm-f-picker-field__text">
                  {{
                    selectSummary(
                      "lockStatus",
                      checksList.lockStatusItems || [],
                      "text"
                    )
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>
        </div>

        <div class="cklm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="cklm-sheet__btn cklm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="cklm-sheet__btn cklm-sheet__btn--ghost"
            @click="backToFilterMain"
          >
            {{ $t("back") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="dateLayerOpen">
      <v-card class="cklm-sheet cklm-sheet--filter">
        <div class="cklm-sheet__handle" />
        <div class="cklm-sheet__header" dir="rtl">
          <span class="cklm-sheet__header-spacer" />
          <div class="cklm-sheet__title">{{ dateLayerLabel }}</div>
          <button
            type="button"
            class="cklm-sheet__back"
            :aria-label="$t('back')"
            @click="dateLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="cklm-mobile-date" dir="rtl">
          <div class="cklm-mobile-date__selected">
            <span class="cklm-mobile-date__label">{{ dateLayerLabel }}</span>
            <strong class="cklm-mobile-date__value">
              {{
                hasValue(checksList[dateLayerField])
                  ? checksList[dateLayerField]
                  : "—"
              }}
            </strong>
            <button
              v-if="hasValue(checksList[dateLayerField])"
              type="button"
              class="cklm-mobile-date__clear"
              @click="checksList[dateLayerField] = null"
            >
              {{ $t("clear") }}
            </button>
          </div>
          <date-picker
            v-if="dateLayerField"
            :key="dateLayerField"
            v-model="checksList[dateLayerField]"
            inline
            simple
            auto-submit
            type="date"
            :locale="$store.state.date_locale"
            :format="$store.state.date_format"
            :min="dateLayerMin"
            class="cklm-mobile-date__picker"
            color="#7B8BA3"
          />
        </div>

        <div class="cklm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="cklm-sheet__btn cklm-sheet__btn--primary"
            @click="dateLayerOpen = false"
          >
            {{ $t("Save") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="selectLayerOpen">
      <v-card class="cklm-sheet cklm-sheet--filter">
        <div class="cklm-sheet__handle" />
        <div class="cklm-sheet__header" dir="rtl">
          <span class="cklm-sheet__header-spacer" />
          <div class="cklm-sheet__title">{{ selectLayerTitle }}</div>
          <button
            type="button"
            class="cklm-sheet__back"
            :aria-label="$t('back')"
            @click="selectLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="cklm-select-layer" dir="rtl">
          <button
            v-for="item in selectLayerItems"
            :key="item.value"
            type="button"
            class="cklm-select-layer__item"
            @click="selectLayerPick(item.value)"
          >
            <span class="cklm-select-layer__text">
              {{ item[selectLayerTextKey] }}
            </span>
            <v-icon
              v-if="isSelectLayerSelected(item.value)"
              size="20"
              color="#5B7C99"
            >
              mdi-check
            </v-icon>
          </button>
          <button
            v-if="
              selectLayerField && hasValue(checksList[selectLayerField])
            "
            type="button"
            class="cklm-select-layer__clear"
            @click="selectLayerClear"
          >
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="deleteSheet">
      <v-card class="cklm-confirm-sheet" dir="rtl">
        <div class="cklm-confirm-sheet__handle" />
        <div class="cklm-confirm-sheet__icon">
          <v-icon size="26" color="#E53935">mdi-trash-can-outline</v-icon>
        </div>
        <div class="cklm-confirm-sheet__title">
          {{ deleteGroupMode ? $t("groupDelete") : $t("delete") }}
        </div>
        <p class="cklm-confirm-sheet__subtitle">
          {{ $t("are-you-sure-delete-this-case") }}
        </p>
        <div v-if="deleteGroupMode" class="cklm-confirm-sheet__rows">
          <div class="cklm-confirm-sheet__row">
            <span>{{ $t("totalCount") }}</span>
            <strong>{{ selectedItems.length }}</strong>
          </div>
          <div class="cklm-confirm-sheet__items">
            <div
              v-for="(sel, si) in selectedItems"
              :key="'del-item-' + si"
              class="cklm-confirm-sheet__item"
            >
              <span class="cklm-confirm-sheet__item-main">
                <span class="cklm-confirm-sheet__item-index">
                  {{ selectedRowNumber(sel) }}
                </span>
                <span class="cklm-confirm-sheet__item-name">
                  {{
                    sel.account && sel.account.name ? sel.account.name : "—"
                  }}
                </span>
              </span>
              <span class="cklm-confirm-sheet__item-number">#{{ sel.id }}</span>
            </div>
          </div>
        </div>
        <div
          v-else-if="checksList.deleteItem"
          class="cklm-confirm-sheet__rows"
        >
          <div class="cklm-confirm-sheet__row">
            <span>{{ $t("account") }}</span>
            <strong>
              {{
                checksList.deleteItem.account &&
                checksList.deleteItem.account.name
                  ? checksList.deleteItem.account.name
                  : "—"
              }}
            </strong>
          </div>
          <div class="cklm-confirm-sheet__row">
            <span>{{ $t("number") }} {{ $t("check") }}</span>
            <strong>{{ checksList.deleteItem.number || "—" }}</strong>
          </div>
          <div class="cklm-confirm-sheet__row">
            <span>{{ $t("due") }}</span>
            <strong>{{ checksList.deleteItem.due_date || "—" }}</strong>
          </div>
          <div class="cklm-confirm-sheet__row">
            <span>{{ $t("amount") }}</span>
            <strong>{{ amountText(checksList.deleteItem) }}</strong>
          </div>
          <div class="cklm-confirm-sheet__row">
            <span>{{ $t("bank") }}</span>
            <strong>{{ bankText(checksList.deleteItem) }}</strong>
          </div>
          <div class="cklm-confirm-sheet__row">
            <span>{{ $t("id") }}</span>
            <strong>{{ checksList.deleteItem.id }}</strong>
          </div>
        </div>
        <div class="cklm-confirm-sheet__actions">
          <v-btn
            color="error"
            block
            depressed
            height="48"
            class="cklm-confirm-sheet__yes"
            @click="confirmDelete"
          >
            {{ $t("yes") }}
          </v-btn>
          <v-btn
            outlined
            block
            height="48"
            color="secondary"
            class="cklm-confirm-sheet__no"
            @click="cancelDelete"
          >
            {{ $t("no") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="statusSheet" scrollable>
      <v-card class="cklm-sheet cklm-sheet--status">
        <div class="cklm-sheet__handle" />
        <div class="cklm-sheet__header" dir="rtl">
          <div class="cklm-sheet__header-side">
            <button
              v-if="statusItem"
              type="button"
              class="cklm-sheet__back"
              :aria-label="$t('history')"
              @click="statusTrackOpen = true"
            >
              <v-icon size="20" color="#5B7C99">mdi-history</v-icon>
            </button>
            <span
              v-if="statusItem && statusItem.type"
              class="cklm-status-type-chip"
            >
              {{ $t("check") }} {{ $t(statusItem.type) }}
            </span>
          </div>
          <div class="cklm-sheet__title">
            {{ $t("change") }} {{ $t("status") }}
          </div>
          <div class="cklm-sheet__header-side cklm-sheet__header-side--end">
            <button
              type="button"
              class="cklm-sheet__back"
              :aria-label="$t('back')"
              @click="statusSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <div v-if="statusItem" class="cklm-status-body" dir="rtl">
          <div class="cklm-status-summary">
            <div class="cklm-status-summary__row">
              <span class="cklm-status-summary__label">{{ $t("account") }}</span>
              <strong class="cklm-status-summary__value">
                {{
                  statusItem.account && statusItem.account.name
                    ? statusItem.account.name
                    : "—"
                }}
              </strong>
            </div>
            <div class="cklm-status-summary__row">
              <span class="cklm-status-summary__label">
                {{ $t("amount") }} {{ $t("check") }}
              </span>
              <strong class="cklm-status-summary__value">
                {{ amountText(statusItem) }}
              </strong>
            </div>
            <div class="cklm-status-summary__row">
              <span class="cklm-status-summary__label">
                {{ $t("number") }} {{ $t("check") }}
              </span>
              <strong class="cklm-status-summary__value">
                {{ statusItem.number || "—" }}
              </strong>
            </div>
            <div class="cklm-status-summary__row">
              <span class="cklm-status-summary__label">
                {{ $t("name") }} {{ $t("bank") }}
              </span>
              <strong class="cklm-status-summary__value">
                {{ statusBankSummary(statusItem) }}
              </strong>
            </div>
            <div class="cklm-status-summary__row cklm-status-summary__row--status">
              <span class="cklm-status-summary__label">{{ $t("status") }}</span>
              <span
                class="cklm-card__status-pill"
                :style="statusChipStyle(statusItem)"
              >
                {{ statusLabel(statusItem) }}
              </span>
            </div>
          </div>

          <div class="cklm-filter-fields cklm-filter-fields--status">
            <div class="cklm-f-field-wrap">
              <div class="cklm-f-field-wrap__label">{{ $t("status") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="statusSelectOpen = true"
              >
                <span class="cklm-f-picker-field__text">
                  {{ statusFormStatusText }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>

            <div class="cklm-f-field-wrap">
              <div class="cklm-f-field-wrap__label">{{ $t("date") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                @click="statusDateOpen = true"
              >
                <span class="cklm-f-picker-field__text">
                  {{ statusForm.date || "—" }}
                </span>
                <span
                  v-if="hasValue(statusForm.date)"
                  class="cklm-picker-cover__clear"
                  @click.stop="statusForm.date = null"
                >
                  <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>

            <div
              v-if="statusForm.type === 'customer'"
              class="cklm-f-field-wrap"
            >
              <div class="cklm-f-field-wrap__label">{{ $t("account") }}</div>
              <button
                type="button"
                class="cklm-f-picker-field"
                :disabled="!statusBankFieldEnabled"
                @click="openStatusBankPicker"
              >
                <span class="cklm-f-picker-field__text">
                  {{
                    statusBankFieldEnabled
                      ? pickerSummary("ckStatusBank", statusForm.bank)
                      : "—"
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cklm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="ckStatusBank"
                  v-model="statusForm.bank"
                  :title="$t('account')"
                  type="allBankCash"
                  :disabled="!statusBankFieldEnabled"
                />
              </div>
            </div>

            <template v-if="statusForm.type === 'own' && statusItem.bank">
              <div class="cklm-status-note">
                <span class="cklm-status-note__label">
                  {{ $t("remain") }} {{ $t("bank") }}
                </span>
                <strong class="cklm-status-note__value">
                  {{ statusBankRemainText(statusItem) }}
                </strong>
              </div>
              <div
                v-if="statusInventoryShortage(statusItem)"
                class="cklm-status-alert"
              >
                <v-icon size="18" color="#E91E63">mdi-alert</v-icon>
                <span>{{ $t("inventoryShortage") }}</span>
              </div>
            </template>
          </div>
        </div>

        <div class="cklm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="cklm-sheet__btn cklm-sheet__btn--primary"
            :disabled="!statusCanSubmit || statusSubmitting"
            @click="submitStatusChange"
          >
            <v-progress-circular
              v-if="statusSubmitting"
              indeterminate
              size="16"
              width="2"
              color="#FFFFFF"
            />
            <template v-else>{{ $t("submit") }}</template>
          </button>
          <button
            type="button"
            class="cklm-sheet__btn cklm-sheet__btn--ghost"
            @click="statusSheet = false"
          >
            {{ $t("cancel") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="statusSelectOpen">
      <v-card class="cklm-sheet cklm-sheet--filter">
        <div class="cklm-sheet__handle" />
        <div class="cklm-sheet__header" dir="rtl">
          <span class="cklm-sheet__header-spacer" />
          <div class="cklm-sheet__title">{{ $t("status") }}</div>
          <button
            type="button"
            class="cklm-sheet__back"
            :aria-label="$t('back')"
            @click="statusSelectOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="cklm-select-layer" dir="rtl">
          <button
            v-for="opt in statusFormItems"
            :key="opt.value"
            type="button"
            class="cklm-select-layer__item"
            :class="{ 'cklm-select-layer__item--disabled': opt.disable }"
            :disabled="!!opt.disable"
            @click="pickStatusFormStatus(opt)"
          >
            <span class="cklm-select-layer__text">{{ opt.text }}</span>
            <v-icon
              v-if="statusForm.status === opt.value"
              size="20"
              color="#5B7C99"
            >
              mdi-check
            </v-icon>
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="statusDateOpen">
      <v-card class="cklm-sheet cklm-sheet--filter">
        <div class="cklm-sheet__handle" />
        <div class="cklm-sheet__header" dir="rtl">
          <span class="cklm-sheet__header-spacer" />
          <div class="cklm-sheet__title">{{ $t("date") }}</div>
          <button
            type="button"
            class="cklm-sheet__back"
            :aria-label="$t('back')"
            @click="statusDateOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="cklm-mobile-date" dir="rtl">
          <div class="cklm-mobile-date__selected">
            <span class="cklm-mobile-date__label">{{ $t("date") }}</span>
            <strong class="cklm-mobile-date__value">
              {{ statusForm.date || "—" }}
            </strong>
            <button
              v-if="hasValue(statusForm.date)"
              type="button"
              class="cklm-mobile-date__clear"
              @click="statusForm.date = null"
            >
              {{ $t("clear") }}
            </button>
          </div>
          <date-picker
            v-if="statusDateOpen"
            key="status-form-date"
            v-model="statusForm.date"
            inline
            simple
            auto-submit
            type="date"
            :locale="$store.state.date_locale"
            :format="$store.state.date_format"
            :min="$store.state.startDate"
            :max="$store.state.endDate"
            class="cklm-mobile-date__picker"
            color="#7B8BA3"
          />
        </div>
        <div class="cklm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="cklm-sheet__btn cklm-sheet__btn--primary"
            @click="statusDateOpen = false"
          >
            {{ $t("Save") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="statusTrackOpen" scrollable>
      <v-card class="cklm-sheet cklm-sheet--status">
        <div class="cklm-sheet__handle" />
        <div class="cklm-sheet__header" dir="rtl">
          <span class="cklm-sheet__header-spacer" />
          <div class="cklm-sheet__title">{{ $t("history") }}</div>
          <button
            type="button"
            class="cklm-sheet__back"
            :aria-label="$t('back')"
            @click="statusTrackOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div v-if="statusItem" class="cklm-status-body" dir="rtl">
          <status-track :check="statusItem" />
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
export default {
  name: "ChecksListMobile",
  inject: ["checksList"],
  components: {
    autocomplete: () =>
      import("../../../../components/general/patial/autocomplete"),
    Tag: () => import("../../../../components/general/Tag/tag"),
    statusTrack: () => import("../../checkStatusTrack"),
  },
  data() {
    return {
      displayItems: [],
      appending: false,
      observer: null,
      detailSheet: false,
      moreSheet: false,
      detailItem: null,
      filterSheet: false,
      filterGroupSheet: false,
      filterGroup: null,
      dateLayerOpen: false,
      dateLayerField: null,
      dateLayerLabel: "",
      selectLayerOpen: false,
      selectLayerField: null,
      selectLayerTitle: "",
      selectLayerItems: [],
      selectLayerTextKey: "text",
      selectLayerMulti: false,
      selectionMode: false,
      selectedItems: [],
      _selectionSynced: false,
      deleteSheet: false,
      deleteGroupMode: false,
      statusSheet: false,
      statusItem: null,
      statusForm: {
        status: null,
        date: null,
        bank: null,
        type: null,
      },
      statusFormItems: [],
      statusSubmitting: false,
      statusSelectOpen: false,
      statusDateOpen: false,
      statusTrackOpen: false,
    };
  },
  computed: {
    currentPage() {
      const opts = this.checksList.tableOptions || {};
      return opts.page || 1;
    },
    lockPermission() {
      return !!this.checksList.lockPermission;
    },
    logType() {
      return this.checksList.typeForm || this.checksList.type;
    },
    accountFilterTitle() {
      return this.checksList.type === "payCheck"
        ? this.$t("reciever")
        : this.$t("payer");
    },
    hasAnyFilter() {
      return (
        this.groupHasValue("parties") ||
        this.groupHasValue("date") ||
        this.groupHasValue("category") ||
        this.groupHasValue("status")
      );
    },
    filterGroups() {
      return [
        {
          key: "parties",
          icon: "mdi-account-outline",
          title: this.accountFilterTitle + " / " + this.$t("bank"),
        },
        {
          key: "date",
          icon: "mdi-calendar",
          title: this.$t("dueDate"),
        },
        {
          key: "category",
          icon: "mdi-shape-outline",
          title: this.$t("tag") + " / " + this.$t("branch"),
        },
        {
          key: "status",
          icon: "mdi-list-status",
          title: this.$t("status"),
        },
      ];
    },
    activeFilterGroupTitle() {
      const group = this.filterGroups.find((g) => g.key === this.filterGroup);
      return group ? group.title : this.$t("filter");
    },
    dateFields() {
      return [
        {
          key: "fromDate",
          label: this.$t("from") + " " + this.$t("dueDate"),
        },
        {
          key: "toDate",
          label: this.$t("until") + " " + this.$t("dueDate"),
        },
      ];
    },
    dateLayerMin() {
      if (!this.dateLayerField) return undefined;
      if (this.dateLayerField === "toDate") {
        return this.checksList.fromDate || undefined;
      }
      return undefined;
    },
    statusFormStatusText() {
      const found = (this.statusFormItems || []).find(
        (i) => i && i.value === this.statusForm.status
      );
      return found ? found.text : "—";
    },
    statusBankFieldEnabled() {
      return (
        this.statusForm.status === "bank" ||
        this.statusForm.status === "passed"
      );
    },
    statusCanSubmit() {
      // Same visibility rule as checkStatus submit btn
      if (!this.statusItem || !this.statusItem.status) return false;
      return this.statusForm.status !== this.statusItem.status.status;
    },
  },
  watch: {
    "checksList.items"(val) {
      const items = val || [];
      if (this.appending) {
        const seen = {};
        this.displayItems.forEach((i) => {
          seen[i.id] = true;
        });
        this.displayItems = this.displayItems.concat(
          items.filter((i) => !seen[i.id])
        );
        this.appending = false;
      } else {
        this.displayItems = items.slice();
      }
    },
    "checksList.search"() {
      this.appending = false;
      if (this.currentPage !== 1) {
        this.checksList.tableOptions = Object.assign(
          {},
          this.checksList.tableOptions,
          { page: 1 }
        );
      }
    },
    "checksList.selected"(val) {
      if (!this.selectionMode || !this._selectionSynced) return;
      if (!val || !val.length) {
        this._selectionSynced = false;
        this.exitSelectionMode();
      }
    },
    deleteSheet(val) {
      if (!val) this.deleteGroupMode = false;
    },
    "checksList.checkAverageDialog"(val) {
      if (!val) return;
      // Keep shell.selected for average calc; just leave selection UI.
      this._selectionSynced = false;
      this.selectionMode = false;
      this.selectedItems = [];
    },
    statusSheet(val) {
      if (val) return;
      this.statusItem = null;
      this.statusFormItems = [];
      this.statusForm = {
        status: null,
        date: null,
        bank: null,
        type: null,
      };
      this.statusSelectOpen = false;
      this.statusDateOpen = false;
      this.statusTrackOpen = false;
      this.statusSubmitting = false;
    },
  },
  mounted() {
    this.displayItems = (this.checksList.items || []).slice();
    this.checksList.tableOptions = Object.assign(
      {},
      this.checksList.tableOptions,
      { page: 1, itemsPerPage: 10 }
    );
    this.$nextTick(() => {
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0] && entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ name: "Home" }).catch(() => {});
    },
    goToNew() {
      const type = this.checksList.type;
      if (!type || type === "removedChecks") return;
      if (typeof this.checksList.goTo === "function") {
        this.checksList.goTo(type);
      }
    },
    onRefreshTap() {
      this.appending = false;
      this.checksList.tableOptions = Object.assign(
        {},
        this.checksList.tableOptions,
        { page: 1 }
      );
      this.checksList.getItems();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onFilterTap() {
      this.filterSheet = true;
    },
    openPicker(refName) {
      const comp = this.$refs[refName];
      if (comp && typeof comp.openMobileDialog === "function") {
        comp.openMobileDialog();
      }
    },
    openSelectLayer(field, title, items, textKey, multi) {
      this.selectLayerField = field;
      this.selectLayerTitle = title;
      this.selectLayerItems = items || [];
      this.selectLayerTextKey = textKey || "text";
      this.selectLayerMulti = !!multi;
      this.selectLayerOpen = true;
    },
    isSelectLayerSelected(value) {
      if (!this.selectLayerField) return false;
      const current = this.checksList[this.selectLayerField];
      if (this.selectLayerMulti) {
        return Array.isArray(current) && current.indexOf(value) !== -1;
      }
      return current === value;
    },
    selectLayerPick(value) {
      if (!this.selectLayerField) return;
      if (this.selectLayerMulti) {
        const current = Array.isArray(this.checksList[this.selectLayerField])
          ? this.checksList[this.selectLayerField].slice()
          : [];
        const idx = current.indexOf(value);
        if (idx === -1) current.push(value);
        else current.splice(idx, 1);
        this.checksList[this.selectLayerField] = current.length
          ? current
          : null;
        return;
      }
      this.checksList[this.selectLayerField] = value;
      this.selectLayerOpen = false;
    },
    selectLayerClear() {
      if (this.selectLayerField) {
        this.checksList[this.selectLayerField] = null;
      }
      this.selectLayerOpen = false;
    },
    selectSummary(field, items, textKey) {
      const v = this.checksList[field];
      if (!this.hasValue(v)) return "—";
      const key = textKey || "text";
      if (Array.isArray(v)) {
        const names = [];
        v.forEach((val) => {
          const found = (items || []).find((i) => i && i.value === val);
          if (found) names.push(found[key]);
        });
        return names.length ? names.join(" · ") : String(v.length);
      }
      const found = (items || []).find((i) => i && i.value === v);
      return found ? found[key] : "—";
    },
    pickerSummary(refName, value) {
      if (!this.hasValue(value)) return "—";
      const comp = this.$refs[refName];
      const items = (comp && comp.items) || [];
      const arr = Array.isArray(value) ? value : [value];
      const names = [];
      arr.forEach((v) => {
        if (v && typeof v === "object") {
          if (v.name || v.text) names.push(v.name || v.text);
        } else {
          const found = items.find((i) => i && (i.id === v || i.value === v));
          if (found && (found.name || found.text)) {
            names.push(found.name || found.text);
          }
        }
      });
      if (names.length) return names.join(" · ");
      return String(arr.length);
    },
    openFilterGroup(key) {
      this.filterGroup = key;
      this.filterSheet = false;
      this.$nextTick(() => {
        this.filterGroupSheet = true;
      });
    },
    backToFilterMain() {
      this.filterGroupSheet = false;
      this.$nextTick(() => {
        this.filterSheet = true;
      });
    },
    hasValue(v) {
      if (Array.isArray(v)) return v.length > 0;
      return v !== null && v !== undefined && v !== "";
    },
    groupHasValue(key) {
      const list = this.checksList;
      if (key === "parties") {
        return (
          this.hasValue(list.account) ||
          this.hasValue(list.bank) ||
          this.hasValue(list.bankRecieve) ||
          this.hasValue(list.currencyFilter) ||
          this.hasValue(list.cashBox)
        );
      }
      if (key === "date") {
        return this.hasValue(list.fromDate) || this.hasValue(list.toDate);
      }
      if (key === "category") {
        return (
          this.hasValue(list.tag) ||
          this.hasValue(list.branch) ||
          this.hasValue(list.registerar)
        );
      }
      if (key === "status") {
        return this.hasValue(list.status) || this.hasValue(list.lockStatus);
      }
      return false;
    },
    groupSubtitle(key) {
      const list = this.checksList;
      if (key === "parties") {
        const parts = [this.accountFilterTitle, this.$t("currency")];
        if (list.type === "payCheck" || list.type === "recieveCheck") {
          parts.splice(1, 0, this.$t("bank"));
        }
        if (list.type === "recieveCheck") parts.push(this.$t("cashBox"));
        return parts.join(" · ");
      }
      if (key === "date") {
        const parts = [];
        if (this.hasValue(list.fromDate)) parts.push(list.fromDate);
        if (this.hasValue(list.toDate)) parts.push(list.toDate);
        return parts.length ? parts.join(" · ") : this.$t("dueDate");
      }
      if (key === "category") {
        return (
          this.$t("tag") +
          " · " +
          this.$t("branch") +
          " · " +
          this.$t("registerar")
        );
      }
      if (key === "status") {
        const parts = [];
        const statusText = this.selectSummary(
          "status",
          list.statusItems || [],
          "text"
        );
        if (statusText !== "—") parts.push(statusText);
        const lockItem = (list.lockStatusItems || []).find(
          (i) => i.value === list.lockStatus
        );
        if (lockItem) parts.push(lockItem.text);
        return parts.length ? parts.join(" · ") : this.$t("status");
      }
      return "";
    },
    openDateLayer(field) {
      this.dateLayerField = field.key;
      this.dateLayerLabel = field.label;
      this.dateLayerOpen = true;
    },
    applyFilter() {
      this.filterSheet = false;
      this.filterGroupSheet = false;
      this.selectLayerOpen = false;
      this.dateLayerOpen = false;
      this.appending = false;
      this.checksList.tableOptions = Object.assign(
        {},
        this.checksList.tableOptions,
        { page: 1 }
      );
      this.checksList.getItems();
    },
    clearFilter() {
      const list = this.checksList;
      list.account = null;
      list.bank = null;
      list.bankRecieve = null;
      list.currencyFilter = null;
      list.cashBox = null;
      list.fromDate = null;
      list.toDate = null;
      list.tag = null;
      list.branch = null;
      list.registerar = null;
      list.status = null;
      list.lockStatus = null;
      this.applyFilter();
    },
    removePermission(type) {
      if (typeof this.checksList.removePermission === "function") {
        return this.checksList.removePermission(type);
      }
      return false;
    },
    statusOf(item) {
      return item && item.status && item.status.status
        ? item.status.status
        : null;
    },
    canEdit(item) {
      if (!item) return false;
      if (this.checksList.type === "removedChecks") return false;
      if (item.lock) return false;
      const st = this.statusOf(item);
      return st === "issued" || st === "fund";
    },
    canDelete(item) {
      if (!item) return false;
      if (this.checksList.type === "removedChecks") return false;
      const st = this.statusOf(item);
      if (st !== "issued" && st !== "fund") return false;
      return this.removePermission(this.checksList.type);
    },
    canChangeStatus(item) {
      if (!item) return false;
      if (this.checksList.type === "removedChecks") return false;
      if (item.kind === "guarantee") return false;
      return true;
    },
    amountText(item) {
      if (!item) return "—";
      const n = item.amount
        ? this.$numberFormat
          ? this.$numberFormat(
              item.amount,
              item.currency && item.currency.decimal
            )
          : item.amount
        : this.$numberFormat
        ? this.$numberFormat(0)
        : "0";
      const cur =
        item.currency && item.currency.name ? " " + item.currency.name : "";
      return n + cur;
    },
    bankText(item) {
      if (!item) return "—";
      if (item.type === "own") {
        if (item.bank && item.bank.name) return item.bank.name;
        return "—";
      }
      if (item.type === "customer") {
        if (item.bank_name) return item.bank_name;
        return "—";
      }
      if (item.bank && item.bank.name) return item.bank.name;
      if (item.bank_name) return item.bank_name;
      return "—";
    },
    statusLabel(item) {
      const st = this.statusOf(item);
      if (!st) return "—";
      let label = this.$t(st);
      if (st === "fund" && item.cashbox && item.cashbox.name) {
        label += " " + item.cashbox.name;
      }
      return label;
    },
    statusChipStyle(item) {
      const color =
        item && item.status && item.status.color
          ? item.status.color
          : "#5B7C99";
      return {
        background: color,
        color: "#ffffff",
      };
    },
    openDetails(item) {
      this.detailItem = item;
      this.detailSheet = true;
    },
    selectedRowNumber(item) {
      if (!item) return "";
      const idx = this.displayItems.findIndex((i) => i.id === item.id);
      return idx >= 0 ? idx + 1 : "";
    },
    askDelete(item) {
      this.moreSheet = false;
      this.detailSheet = false;
      this.deleteGroupMode = false;
      this.checksList.selected = [];
      this._selectionSynced = false;
      this.checksList.deleteItem = item;
      this.deleteSheet = true;
    },
    askGroupDelete() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.checksList.deleteItem = null;
      this.deleteGroupMode = true;
      this.deleteSheet = true;
    },
    cancelDelete() {
      this.deleteSheet = false;
      this.deleteGroupMode = false;
    },
    confirmDelete() {
      this.deleteSheet = false;
      if (this.checksList.selected && this.checksList.selected.length > 0) {
        this.checksList.removegroup();
      } else {
        this.checksList.remove();
      }
      if (this.deleteGroupMode) {
        this.deleteGroupMode = false;
        this.exitSelectionMode();
      }
    },
    bulkLock(type) {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      if (typeof this.checksList.lockGroup === "function") {
        this.checksList.lockGroup(type);
      }
    },
    bulkRefreshRemoved() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      if (typeof this.checksList.refreshRemovedItem === "function") {
        this.checksList.refreshRemovedItem();
      }
    },
    bulkCheckAverage() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this._selectionSynced = false;
      if (typeof this.checksList.fetchCheckAverageFirst === "function") {
        this.checksList.fetchCheckAverageFirst();
      }
    },
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.selectedItems = [];
        this._selectionSynced = false;
        this.checksList.selected = [];
      }
    },
    exitSelectionMode() {
      this.selectionMode = false;
      this.selectedItems = [];
      this._selectionSynced = false;
      this.checksList.selected = [];
    },
    isSelected(item) {
      return this.selectedItems.some((i) => i.id === item.id);
    },
    toggleSelect(item) {
      if (this.isSelected(item)) {
        this.selectedItems = this.selectedItems.filter(
          (i) => i.id !== item.id
        );
      } else {
        this.selectedItems = this.selectedItems.concat([item]);
      }
    },
    selectAllLoaded() {
      const seen = {};
      this.selectedItems.forEach((i) => {
        seen[i.id] = true;
      });
      this.selectedItems = this.selectedItems.concat(
        this.displayItems.filter((i) => !seen[i.id])
      );
    },
    clearSelection() {
      this.selectedItems = [];
    },
    syncSelection() {
      this.checksList.selected = this.selectedItems.slice();
      this._selectionSynced = true;
    },
    openStatusSheet(item) {
      if (!this.canChangeStatus(item)) return;
      this.moreSheet = false;
      this.detailSheet = false;
      this.statusItem = item;
      this.initStatusForm(item);
      this.statusSheet = true;
    },
    initStatusForm(item) {
      // Same statusItems / initial values rules as checkStatus.show()
      if (!item) return;
      let passedDisabled = true;
      if (
        item.type === "own" &&
        item.bank &&
        item.bank.remain &&
        parseFloat(item.bank.remain.currency_remain) >=
          parseFloat(Number(item.amount))
      ) {
        passedDisabled = false;
      }
      let items = [];
      if (item.type === "own") {
        items = [
          { value: "issued", text: this.$t("issued") },
          { value: "returned", text: this.$t("returned") },
          {
            value: "passed",
            text: this.$t("passed"),
            disable: passedDisabled,
          },
          { value: "canceled", text: this.$t("canceled") },
        ];
      } else {
        let payedDisable = false;
        if (
          (item.status &&
            (item.status.status === "payed" ||
              item.status.status === "passed")) ||
          item.kind === "guarantee"
        ) {
          payedDisable = true;
        }
        items = [
          { value: "fund", text: this.$t("fund") },
          {
            value: "bank",
            text: this.$t("inBank"),
            disable: payedDisable,
          },
          {
            value: "returned",
            text: this.$t("returned"),
            disable: payedDisable,
          },
          {
            value: "passed",
            text: this.$t("passed"),
            disable: payedDisable,
          },
          {
            value: "canceled",
            text: this.$t("canceled"),
            disable: payedDisable,
          },
          {
            value: "payed",
            text: this.$t("payed"),
            disable: true,
          },
        ];
      }
      this.statusFormItems = items;
      this.statusForm = {
        status: item.status && item.status.status ? item.status.status : null,
        date: item.status && item.status.date ? item.status.date : null,
        bank: item.status && item.status.bank ? item.status.bank : null,
        type: item.type,
      };
    },
    statusBankSummary(item) {
      if (!item) return "—";
      if (item.type === "own") {
        const name = item.bank && item.bank.name ? item.bank.name : "";
        const number = item.bank && item.bank.number ? item.bank.number : "";
        const text = [name, number].filter(Boolean).join("-");
        return text || "—";
      }
      const name = item.bank_name || "";
      const number = item.bank_account_number || "";
      const text = [name, number].filter(Boolean).join("-");
      return text || "—";
    },
    statusBankRemainText(item) {
      if (!item || !item.bank || !item.bank.remain) return "0";
      const remain = item.bank.remain;
      const amount = this.$numberFormat
        ? this.$numberFormat(remain.currency_remain)
        : remain.currency_remain;
      const symbol =
        remain.currency && remain.currency.symbol
          ? " " + remain.currency.symbol
          : "";
      return amount + symbol;
    },
    statusInventoryShortage(item) {
      if (!item || !item.bank || !item.bank.remain) return false;
      return (
        parseFloat(item.bank.remain.currency_remain) <
        parseFloat(Number(item.amount))
      );
    },
    pickStatusFormStatus(opt) {
      if (!opt || opt.disable) return;
      this.statusForm.status = opt.value;
      this.statusSelectOpen = false;
    },
    openStatusBankPicker() {
      if (!this.statusBankFieldEnabled) return;
      this.openPicker("ckStatusBank");
    },
    applyStatusUpdate(status) {
      if (!this.statusItem || !status) return;
      const id = this.statusItem.id;
      const patch = (arr) => {
        if (!arr || !arr.length) return;
        const idx = arr.findIndex((i) => i && i.id === id);
        if (idx >= 0) {
          this.$set(arr[idx], "status", status);
        }
      };
      patch(this.displayItems);
      patch(this.checksList.items);
      if (this.detailItem && this.detailItem.id === id) {
        this.$set(this.detailItem, "status", status);
      }
      if (this.statusItem && this.statusItem.id === id) {
        this.$set(this.statusItem, "status", status);
      }
    },
    submitStatusChange() {
      // Same endpoint + payload fields used by checkStatus.update()
      if (!this.statusCanSubmit || !this.statusItem || this.statusSubmitting) {
        return;
      }
      const app = this;
      app.statusSubmitting = true;
      const payload = Object.assign({}, app.statusForm, {
        changeStatus: true,
        item: app.statusItem,
        statusItems: app.statusFormItems,
      });
      app.$axios
        .put(app.$endpoint + "checks/" + app.statusItem.id, payload)
        .then((res) => {
          res = res.data;
          const nextStatus = res && res.data ? res.data : null;
          if (nextStatus) app.applyStatusUpdate(nextStatus);
          app.statusSheet = false;
        })
        .catch((err) => {
          if (typeof app.checksList.serverErrorHandeler === "function") {
            app.checksList.serverErrorHandeler(err);
          }
        })
        .finally(() => {
          app.statusSubmitting = false;
        });
    },
    copyLink(item) {
      if (typeof this.checksList.editLink === "function") {
        return this.checksList.editLink(item);
      }
      return "";
    },
    notifyCopied() {
      if (typeof this.checksList.copied === "function") {
        this.checksList.copied();
      }
    },
    onCopyLinkQuick() {
      this.notifyCopied();
    },
    openAccountTurnover(item) {
      if (!item || !item.account || !item.account.fin_id) return;
      this.detailSheet = false;
      this.moreSheet = false;
      this.checksList.openComponent(
        "accountingTurnover",
        item.account.fin_id
      );
    },
    runNamedAction(name) {
      const item = this.detailItem;
      if (!item) return;
      this.moreSheet = false;
      if (name === "lock") {
        this.detailSheet = false;
        this.checksList.lock(item, item.lock ? "unlock" : "lock");
        return;
      }
      if (name === "status") {
        this.openStatusSheet(item);
        return;
      }
      if (name === "viewDoc") {
        this.detailSheet = false;
        this.checksList.viewDocument(item);
        return;
      }
      if (name === "receipt") {
        this.detailSheet = false;
        this.checksList.receiptPrint(item);
        return;
      }
      if (name === "chequePrint") {
        this.detailSheet = false;
        this.checksList.chequePrint(item);
        return;
      }
      if (name === "print") {
        this.detailSheet = false;
        this.checksList.documentPrint(item);
        return;
      }
      this.detailSheet = false;
      if (name === "edit") this.checksList.edit(item);
    },
    detailRows(item) {
      if (!item) return [];
      const rows = [];
      const push = (label, value, kind) =>
        rows.push({
          label,
          value: value == null || value === "" ? "—" : value,
          kind: kind || null,
        });
      // Desktop header order (account in name-block; relations/tags as chips).
      push(this.$t("id"), item.id, "band");
      push(
        this.$t("document-number"),
        item.doc_header && item.doc_header.number,
        "band"
      );
      push(this.$t("number") + " " + this.$t("check"), item.number, "band");
      push(this.$t("bank"), this.bankText(item));
      push(this.$t("kind"), item.kind ? this.$t(item.kind) : null);
      push(this.$t("date"), item.date);
      push(this.$t("due"), item.due_date);
      push(this.$t("owner"), item.owner_name);
      push(this.$t("fishermanNumber"), item.fisherman_number);
      push(
        this.$t("branch"),
        item.branch && item.branch.name ? item.branch.name : item.branch
      );
      push(this.$t("amount"), this.amountText(item), "amount");
      push(
        this.$t("currency"),
        item.currency && item.currency.name
      );
      push(this.$t("status"), this.statusLabel(item));
      push(this.$t("content") || this.$t("description"), item.content);
      if (item.user) {
        push(
          this.$t("registerar"),
          item.user.name ||
            (
              (item.user.firstname || "") +
              " " +
              (item.user.lastname || "")
            ).trim()
        );
      } else {
        push(this.$t("registerar"), null);
      }
      return rows;
    },
    loadMore() {
      if (!this.displayItems.length) return;
      if (this.checksList.loading || this.appending) return;
      if (this.currentPage >= (this.checksList.pageCount || 1)) return;
      this.appending = true;
      this.checksList.tableOptions = Object.assign(
        {},
        this.checksList.tableOptions,
        { page: this.currentPage + 1 }
      );
    },
  },
};
</script>

<style scoped>
.cklm {
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
  background: #ffffff;
  min-height: calc(100vh - 52px);
  box-sizing: border-box;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}
.cklm--selecting .cklm__list {
  padding-bottom: 88px;
}
.cklm__header {
  position: sticky;
  top: 52px;
  z-index: 4;
  background: #ffffff;
  padding: 6px 0 10px;
  border-bottom: 1px solid #eef1f4;
  margin-bottom: 10px;
}
.cklm__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}
.cklm__bar-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 80px;
}
.cklm__bar-btn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.cklm__bar-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.cklm__bar-btn--create {
  background: rgba(91, 124, 153, 0.1);
}
.cklm__bar-btn--back {
  margin-inline-start: auto;
}
.cklm__title {
  flex: 1 1 auto;
  margin: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.cklm__search-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cklm__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f3f5f7;
}
.cklm__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #37474f;
}
.cklm__search-clear {
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
}
.cklm__filter-btn {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border: 1px solid #e0e4e8;
}
.cklm__filter-btn:active {
  background: rgba(91, 124, 153, 0.08);
}
.cklm__filter-btn--active {
  background: rgba(21, 101, 192, 0.1);
  border-color: rgba(21, 101, 192, 0.35);
}
.cklm__select-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 10px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
}
.cklm__select-bar-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 6px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cklm__select-bar-btn:active {
  background: rgba(91, 124, 153, 0.12);
}
.cklm__select-count {
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.cklm__state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
  color: #607d8b;
  font-size: 13px;
}
.cklm__state--empty {
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(100vh - 220px);
  padding: 32px 16px 24px;
  background: #ffffff;
}
.cklm__state--more {
  padding: 14px 0;
}
.cklm__empty-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.85;
}
.cklm__empty-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
  text-align: center;
}
.cklm__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}
.cklm__sentinel {
  height: 1px;
}
.cklm-card {
  position: relative;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
  transition: transform 0.15s ease;
}
.cklm-card--selected {
  transform: scale(0.99);
  border-color: rgba(21, 101, 192, 0.45);
}
.cklm-card__select-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cklm-card__select-overlay--on {
  background: rgba(38, 50, 56, 0.12);
}
.cklm-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0d47a1;
  box-shadow: 0 2px 6px rgba(13, 71, 161, 0.4);
}
.cklm-bulkbar {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.14);
}
.cklm-bulkbar__btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
  padding: 6px 2px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cklm-bulkbar__btn:active {
  background: rgba(91, 124, 153, 0.15);
}
.cklm-bulkbar__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.cklm-bulkbar__btn--danger {
  color: #e53935;
  background: rgba(229, 57, 53, 0.06);
}
.cklm-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.cklm-card__title-block {
  min-width: 0;
  flex: 1 1 auto;
}
.cklm-card__name-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cklm-card__name-link:disabled {
  cursor: default;
}
.cklm-card__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: right;
}
.cklm-card__sub {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.cklm-card__number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 10px;
  padding: 2px 8px;
}
.cklm-card__amounts {
  margin-top: 10px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cklm-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.cklm-card__amount-label {
  font-size: 12px;
  color: #78909c;
}
.cklm-card__amount-value {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
  padding: 2px 8px;
  border-radius: 8px;
}
.cklm-card__amount-value--money {
  background: rgba(91, 124, 153, 0.1);
  color: #37474f;
}
.cklm-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.cklm-card__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
}
.cklm-card__status-pill {
  flex: 0 0 auto;
  max-width: 70%;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 10px !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}
.cklm-card__status-pill--empty {
  background: rgba(144, 164, 174, 0.12);
  color: #90a4ae;
}
.cklm-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 34px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 0;
  -webkit-tap-highlight-color: transparent;
}
.cklm-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.cklm-btn--danger {
  border: 1px solid rgba(229, 57, 53, 0.35);
  background: rgba(229, 57, 53, 0.06);
  color: #c62828;
}
.cklm-btn--primary {
  background: #5b7c99;
  color: #ffffff;
}
.cklm-btn--outline {
  background: #ffffff;
  color: #5b7c99;
  border: 1px solid #cfd8dc;
}
.cklm-pay-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.08);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cklm-pay-btn:active {
  background: rgba(91, 124, 153, 0.14);
}
.cklm-sheet--status {
  max-height: 92vh;
  border-radius: 20px 20px 0 0 !important;
}
.cklm-status-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 12px 12px;
  background: #ffffff;
}
.cklm-status-type-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid #43a047;
  background: #ffffff;
  color: #2e7d32;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.cklm-status-summary {
  margin: 6px 4px 12px;
  padding: 4px 12px;
  border-radius: 10px;
  background: #edf2f7;
}
.cklm-status-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(91, 124, 153, 0.12);
}
.cklm-status-summary__row:last-child {
  border-bottom: 0;
}
.cklm-status-summary__label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.cklm-status-summary__value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #263238;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cklm-status-summary__row--status .cklm-card__status-pill {
  margin-inline-start: auto;
}
.cklm-filter-fields--status {
  padding-top: 4px;
}
.cklm-f-picker-field:disabled {
  opacity: 0.45;
  cursor: default;
  background: #f5f7f9;
}
.cklm-status-note {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 44px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.08);
}
.cklm-status-note__label {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.cklm-status-note__value {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.cklm-status-alert {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(233, 30, 99, 0.08);
  color: #c2185b;
  font-size: 12px;
  font-weight: 700;
}
.cklm-select-layer__item--disabled {
  opacity: 0.4;
  cursor: default;
}
.cklm-sheet__btn--primary:disabled {
  opacity: 0.45;
  cursor: default;
}
.cklm-sheet {
  border-radius: 10px 10px 0 0 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.cklm-sheet__handle {
  flex: 0 0 auto;
  width: 44px;
  height: 4px;
  border-radius: 10px;
  background: #cfd8dc;
  margin: 10px auto 2px;
}
.cklm-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 10px;
  border-bottom: 1px solid #eef1f4;
}
.cklm-sheet__header-side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.cklm-sheet__header-side--end {
  justify-content: flex-end;
}
.cklm-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.cklm-sheet__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.cklm-sheet__num-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 10px;
  background: rgba(135, 158, 182, 0.14);
  border: 1px solid rgba(135, 158, 182, 0.28);
  color: #5b7c99;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.cklm-sheet__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.cklm-sheet__back:active {
  background: rgba(91, 124, 153, 0.1);
}
.cklm-sheet__quickbar {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
}
.cklm-sheet__quick {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 46px;
  padding: 4px 2px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.cklm-sheet__quick .v-icon {
  font-size: 18px !important;
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}
.cklm-sheet__quick:active {
  background: rgba(91, 124, 153, 0.14);
}
.cklm-sheet__quick:disabled {
  opacity: 0.45;
  cursor: default;
}
.cklm-sheet__name-block {
  flex: 0 0 auto;
  background: #edf2f7;
  border-radius: 10px;
  padding: 10px 14px;
  margin: 6px 16px 0;
}
.cklm-sheet__name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.cklm-sheet__name-value {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 70%;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cklm-sheet__name-block .cklm-sheet__linkrow {
  margin: 8px 0 0;
  background: rgba(255, 255, 255, 0.75);
}
.cklm-sheet__name-block .cklm-sheet__linkrow:active {
  background: rgba(255, 255, 255, 0.95);
}
.cklm-sheet__linkrow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  margin: 6px 0 2px;
  padding: 0 10px;
  border: 1px solid rgba(0, 150, 136, 0.16);
  border-radius: 10px;
  background: rgba(0, 150, 136, 0.07);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cklm-sheet__linkrow:active {
  background: rgba(0, 150, 136, 0.15);
}
.cklm-sheet__linkrow-text {
  flex: 1 1 auto;
  font-size: 12.5px;
  font-weight: 700;
  color: #00796b;
  text-align: right;
}
.cklm-sheet__body {
  overflow-y: auto;
  padding: 8px 16px 20px;
}
.cklm-sheet__card {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 4px 14px;
}
.cklm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
}
.cklm-sheet__card .cklm-sheet__row:last-child {
  border-bottom: 0;
}
.cklm-sheet__row--band {
  margin: 0 -14px;
  padding: 8px 14px;
  border-bottom-color: rgba(91, 124, 153, 0.12);
  background: rgba(91, 124, 153, 0.05);
}
.cklm-sheet__row--money {
  background: rgba(91, 124, 153, 0.1);
  border-bottom-color: rgba(91, 124, 153, 0.18);
}
.cklm-sheet__row-label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.cklm-sheet__row-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.cklm-sheet__tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
  max-width: 62%;
}
.cklm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.cklm-sheet__actions--sheet {
  padding-top: 4px;
}
.cklm-sheet__action {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 42px;
  padding: 0 6px;
  border: 0;
  border-bottom: 1px solid #e8edf2;
  border-radius: 10px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.cklm-sheet__action:active {
  background: rgba(91, 124, 153, 0.08);
}
.cklm-sheet__action--log {
  cursor: default;
  padding: 0;
}
.cklm-sheet__action--log > div {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.cklm-sheet__action--log ::v-deep .v-list-item {
  flex: 1 1 auto;
  min-height: 42px;
  height: 42px;
  padding: 0;
}
.cklm-sheet__action--log ::v-deep .v-list-item__icon {
  margin: 0 !important;
  min-width: 19px;
  align-self: center;
}
.cklm-sheet__action--log ::v-deep .v-list-item__icon .v-icon {
  font-size: 19px !important;
  color: #5b7c99 !important;
}
.cklm-sheet__action--log ::v-deep .v-list-item__title {
  margin-inline-start: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
}
.cklm-sheet--filter {
  max-height: 92vh;
  border-radius: 20px 20px 0 0 !important;
}
.cklm-sheet__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef1f4;
  background: #ffffff;
}
.cklm-sheet__btn {
  flex: 1 1 0;
  height: 44px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cklm-sheet__btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.cklm-sheet__btn--primary:active {
  background: #4d6a84;
}
.cklm-sheet__btn--ghost {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #546e7a;
}
.cklm-sheet__btn--ghost:active {
  background: rgba(91, 124, 153, 0.08);
}
.cklm-filter-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #ffffff;
}
.cklm-filter-nav__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cklm-filter-nav__item:active {
  background: #f7f9fc;
}
.cklm-filter-nav__item:last-child {
  border-bottom: 0;
}
.cklm-filter-nav__icon {
  flex: 0 0 auto;
}
.cklm-filter-nav__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.cklm-filter-nav__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}
.cklm-filter-nav__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cklm-filter-nav__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.cklm-filter-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px 6px;
  background: #ffffff;
}
.cklm-filter-fields > * {
  margin-bottom: 14px;
}
.cklm-picker-host {
  display: none !important;
}
.cklm-picker-cover__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}
.cklm-f-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.cklm-f-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 4px;
}
.cklm-f-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.cklm-f-picker-field:active {
  background: #eef4f8;
  border-color: #90caf9;
}
.cklm-f-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cklm-select-layer {
  padding: 6px 16px 20px;
}
.cklm-select-layer__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.cklm-select-layer__text {
  font-size: 13.5px;
  font-weight: 500;
  color: #37474f;
}
.cklm-select-layer__clear {
  display: block;
  width: 100%;
  padding: 14px 4px 4px;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #c62828;
  text-align: center;
  cursor: pointer;
}
.cklm-mobile-date {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
  background: #ffffff;
}
.cklm-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #f7f9fc;
}
.cklm-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.cklm-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
}
.cklm-mobile-date__clear {
  border: 0;
  background: transparent;
  color: #e91e63;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
}
.cklm-mobile-date__picker {
  width: 100%;
}
.cklm-mobile-date__picker ::v-deep .vpd-main,
.cklm-mobile-date__picker ::v-deep .vpd-wrapper,
.cklm-mobile-date__picker ::v-deep .vpd-container,
.cklm-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
}
.cklm-confirm-sheet {
  border-radius: 20px 20px 0 0 !important;
  padding: 8px 16px 16px;
  background: #fff !important;
}
.cklm-confirm-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 14px;
  border-radius: 999px;
  background: #cfd8dc;
}
.cklm-confirm-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.cklm-confirm-sheet__title {
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  color: #212121;
  margin: 0 0 8px;
}
.cklm-confirm-sheet__subtitle {
  margin: 0 0 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
  line-height: 1.6;
}
.cklm-confirm-sheet__rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}
.cklm-confirm-sheet__items {
  max-height: 168px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 4px 12px;
}
.cklm-confirm-sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid #e8edf2;
}
.cklm-confirm-sheet__item:last-child {
  border-bottom: 0;
}
.cklm-confirm-sheet__item-main {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.cklm-confirm-sheet__item-index {
  flex: 0 0 auto;
  min-width: 16px;
  font-size: 11px;
  font-weight: 700;
  color: #90a4ae;
  text-align: center;
}
.cklm-confirm-sheet__item-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  font-weight: 600;
  color: #37474f;
}
.cklm-confirm-sheet__item-number {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  color: #5b7c99;
}
.cklm-confirm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f5f7f9;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
}
.cklm-confirm-sheet__row strong {
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
}
.cklm-confirm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cklm-confirm-sheet__yes {
  border-radius: 12px !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
}
.cklm-confirm-sheet__no {
  border-radius: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}
</style>
